import React from "react";
import { useParams, Link } from "react-router-dom";
import { ROUTE_NAMES } from "../../routes/routeConstants";
import SheetGround from "../../components/shared/ui/SheetGround";
import { VELLUM as T, SANS, MONO, display, mono, body, btn } from "../../components/shared/folio/kit";
import { getArticleBySlug, getRelatedArticles } from "./blogArticles";
import { useAuth } from "../../provider/AuthProvider";
import { useLoginModal } from "../../context/LoginModalContext";

/* An essay, drawn on the same sheet as the schedule that indexes it.

   The plate, the ruled title block and the hairline columns are the folio
   vocabulary the blog index is set in; the reading column is the one place
   the measure is allowed to narrow, because a column of prose is read and
   the rest of the sheet is scanned. The article body is composed from the
   shared Heading/BodyText/BulletList components, which carry legacy utility
   classes from an earlier system - those are overridden inside this scope
   only, since the same components draw six other surfaces. */

const P = "ba";

export const BlogArticlePage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { user } = useAuth();
  const { openLoginModal } = useLoginModal();
  const article = slug ? getArticleBySlug(slug) : undefined;
  const related = slug ? getRelatedArticles(slug, 3) : [];

  if (!article) {
    return (
      <SheetGround className={`${P}-root`} style={{ fontFamily: SANS, color: T.ink }}>
        <section style={{ maxWidth: 1280, margin: "0 auto", padding: "120px 20px", textAlign: "center" }}>
          <div style={{ ...mono(T, T.accent), marginBottom: 18 }}>§ 404 · Not found</div>
          <h1 style={{ ...display(T, "clamp(38px, 6vw, 72px)"), lineHeight: 0.98 }}>Article not found.</h1>
          <p style={{ ...body(T), fontSize: 17, margin: "26px auto 34px", maxWidth: 520 }}>
            The essay you are looking for does not exist or has been retired.
          </p>
          <Link
            to={ROUTE_NAMES.PAGES.BLOG}
            className={`${P}-cta`}
            style={{ ...btn(T, "invert"), textDecoration: "none" }}
          >
            Back to the blog <span aria-hidden style={{ fontSize: 13 }}>→</span>
          </Link>
        </section>
      </SheetGround>
    );
  }

  const meta: [string, string][] = [
    ["By", article.author],
    ["Published", article.publishDate],
    ["Read", article.readTime],
    ["Filed", article.category],
  ];

  return (
    <SheetGround className={`${P}-root`} style={{ fontFamily: SANS, color: T.ink }}>
      {/* The trail back up the sheet */}
      <section style={{ borderBottom: `1px solid ${T.rule}` }}>
        <div
          style={{
            ...mono(T),
            maxWidth: 1280,
            margin: "0 auto",
            padding: "14px 20px",
            display: "flex",
            gap: 10,
            flexWrap: "wrap",
          }}
        >
          <Link to={ROUTE_NAMES.PAGES.HOME} className={`${P}-crumb`} style={{ color: T.mute, textDecoration: "none" }}>
            Index
          </Link>
          <span aria-hidden>/</span>
          <Link to={ROUTE_NAMES.PAGES.BLOG} className={`${P}-crumb`} style={{ color: T.mute, textDecoration: "none" }}>
            Blog
          </Link>
          <span aria-hidden>/</span>
          <span style={{ color: T.ink }}>{article.category}</span>
        </div>
      </section>

      {/* The sheet head */}
      <section style={{ borderBottom: `1.5px solid ${T.ink}` }}>
        <div style={{ maxWidth: 1280, margin: "0 auto", padding: "48px 20px 44px" }}>
          <div style={{ ...mono(T, T.accent), marginBottom: 18, display: "flex", alignItems: "center", gap: 10 }}>
            <span aria-hidden style={{ width: 28, height: 1, background: T.accent }} />
            § Essay · {article.category}
          </div>

          <h1
            style={{
              ...display(T, "clamp(34px, 5.8vw, 78px)"),
              lineHeight: 0.99,
              letterSpacing: "-0.04em",
              maxWidth: 1080,
            }}
          >
            {article.title}
            <span style={{ color: T.accent }}>.</span>
          </h1>

          <p style={{ ...body(T), fontSize: 19, lineHeight: 1.56, maxWidth: 760, marginTop: 26 }}>{article.excerpt}</p>

          {/* The title block, laid on its side */}
          <div
            className={`${P}-meta`}
            style={{
              marginTop: 34,
              borderTop: `1px solid ${T.rule}`,
              borderBottom: `1px solid ${T.rule}`,
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
            }}
          >
            {meta.map(([k, v], i) => (
              <div
                key={k}
                style={{
                  padding: "13px 16px 13px 0",
                  paddingLeft: i === 0 ? 0 : 16,
                  borderLeft: i === 0 ? "none" : `1px solid ${T.rule}`,
                }}
              >
                <div style={{ ...mono(T, T.mute), fontSize: 9, marginBottom: 5 }}>{k}</div>
                <div style={{ fontFamily: MONO, fontSize: 12.5, color: T.ink, letterSpacing: "0.02em" }}>{v}</div>
              </div>
            ))}
          </div>

          {/* The plate, framed as it is on the schedule */}
          <div
            aria-hidden
            className={`${P}-plate`}
            style={{
              marginTop: 34,
              aspectRatio: "21 / 8",
              background: T.panel,
              backgroundImage: `url(${article.image})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              border: `1.5px solid ${T.ink}`,
            }}
          />
        </div>
      </section>

      {/* Reading column and margin */}
      <section>
        <div
          className={`${P}-grid`}
          style={{
            maxWidth: 1280,
            margin: "0 auto",
            padding: "52px 20px 84px",
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) 300px",
            gap: 60,
          }}
        >
          <article className={`${P}-body`} style={{ maxWidth: 720 }}>
            {article.content}
          </article>

          <aside
            className={`${P}-aside`}
            style={{ display: "grid", gap: 30, alignSelf: "start", position: "sticky", top: 110 }}
          >
            {related.length > 0 && (
              <div>
                <div
                  style={{
                    ...mono(T),
                    borderBottom: `1.5px solid ${T.ink}`,
                    paddingBottom: 10,
                    marginBottom: 4,
                  }}
                >
                  § Related from the register
                </div>
                <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                  {related.map((r) => (
                    <li key={r.slug}>
                      <Link
                        to={`${ROUTE_NAMES.PAGES.BLOG}/${r.slug}`}
                        className={`${P}-rel`}
                        style={{
                          display: "block",
                          padding: "16px 0",
                          borderBottom: `1px solid ${T.rule}`,
                          color: T.ink,
                          textDecoration: "none",
                        }}
                      >
                        <div style={{ ...mono(T), fontSize: 9.5, marginBottom: 6 }}>
                          {r.category} · {r.readTime}
                        </div>
                        <div
                          className={`${P}-rel-title`}
                          style={{ ...display(T, "16.5px"), lineHeight: 1.26, letterSpacing: "-0.02em" }}
                        >
                          {r.title}
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div style={{ border: `1.5px solid ${T.ink}`, background: T.ground, padding: 26 }}>
              <div style={{ ...mono(T, T.accent), marginBottom: 10 }}>§ Looking for a role?</div>
              <h4 style={{ ...display(T, "23px"), lineHeight: 1.15, marginBottom: 10 }}>The register is open.</h4>
              <p style={{ ...body(T), fontSize: 14.5, lineHeight: 1.55, marginBottom: 20 }}>
                Browse academic listings from verified UK institutions.
              </p>
              <Link
                to={ROUTE_NAMES.PAGES.JOBS}
                className={`${P}-cta`}
                style={{ ...btn(T, "accent"), width: "100%", justifyContent: "center", textDecoration: "none" }}
                onClick={(e) => {
                  if (!user) {
                    e.preventDefault();
                    openLoginModal();
                  }
                }}
              >
                Browse listings
              </Link>
            </div>
          </aside>
        </div>
      </section>

      <style>{`
        /* The reading column.

           Heading/BodyText/BulletList carry utility classes from the legacy
           system (text-gray-700, text-ink, text-cambridge). They are scoped
           out here rather than edited at source, because the same three
           components draw six other surfaces. Every selector below is one
           class plus one type, which outranks a bare utility class. */
        .${P}-body {
          font-family: ${SANS};
          font-size: 17.5px;
          line-height: 1.72;
          color: ${T.ink};
        }
        .${P}-body h1, .${P}-body h2, .${P}-body h3, .${P}-body h4 {
          font-family: ${SANS};
          font-weight: 700;
          color: ${T.ink};
          letter-spacing: -0.03em;
          line-height: 1.14;
        }
        .${P}-body h2 { font-size: 30px; margin: 2.1em 0 0.62em; }
        .${P}-body h3 { font-size: 25px; margin: 2.05em 0 0.6em; }
        .${P}-body h4 { font-size: 19px; margin: 1.9em 0 0.55em; }
        .${P}-body p {
          font-size: 17.5px;
          line-height: 1.72;
          color: ${T.ink};
          margin: 0 0 1.3em;
        }
        .${P}-body strong { color: ${T.ink}; font-weight: 700; }
        .${P}-body a {
          color: ${T.accent};
          text-decoration: underline;
          text-underline-offset: 3px;
          text-decoration-thickness: 1px;
        }
        .${P}-body ul, .${P}-body ol {
          font-size: 17.5px;
          line-height: 1.68;
          color: ${T.ink};
          margin: 0 0 1.3em;
        }
        .${P}-body li { color: ${T.ink}; margin: 0 0 0.55em; }
        /* Both bullet variants render their text in a span; the check
           variant's icon is the only svg in the column. */
        .${P}-body li span { color: inherit; }
        .${P}-body li::marker { color: ${T.accent}; }
        .${P}-body li svg { color: ${T.accent}; }
        .${P}-body blockquote {
          border-left: 2px solid ${T.accent};
          padding: 2px 0 2px 18px;
          margin: 1.6em 0;
          color: ${T.mute};
        }

        /* An entry answers by inking, never by lifting. */
        .${P}-rel, .${P}-rel-title, .${P}-crumb { transition: color 140ms ease, border-color 160ms ease; }
        .${P}-rel:hover { border-bottom-color: ${T.ink}; }
        .${P}-rel:hover .${P}-rel-title { color: ${T.accent}; }
        .${P}-crumb:hover { color: ${T.accent}; }
        .${P}-cta { transition: background 160ms ease; }
        .${P}-cta:hover { background: ${T.accentDeep}; }

        .${P}-rel:focus-visible, .${P}-crumb:focus-visible, .${P}-cta:focus-visible {
          outline: 2px solid ${T.accent};
          outline-offset: 3px;
        }

        @media (prefers-reduced-motion: reduce) {
          .${P}-rel, .${P}-rel-title, .${P}-crumb, .${P}-cta { transition: none; }
        }

        @media (max-width: 1024px) {
          .${P}-grid { grid-template-columns: 1fr !important; gap: 48px !important; }
          .${P}-aside { position: static !important; }
        }
        @media (max-width: 640px) {
          .${P}-meta { grid-template-columns: repeat(2, 1fr) !important; }
          .${P}-meta > div:nth-child(2n+1) { border-left: none !important; padding-left: 0 !important; }
          .${P}-meta > div:nth-child(-n+2) { border-bottom: 1px solid ${T.rule}; }
          .${P}-plate { aspect-ratio: 16 / 10 !important; }
          .${P}-body { font-size: 16.5px; }
          .${P}-body p, .${P}-body ul, .${P}-body ol { font-size: 16.5px; }
        }
      `}</style>
    </SheetGround>
  );
};
